import type { ComponentPropsWithoutRef } from 'react'
import { LazyImage } from '@/scopes/common/components/Image'
import { space, SpaceBetween, tok } from '@sunwebgroup/ui-components'
import { IconButton } from '@sunwebgroup/ui-components/uds'
import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { styled } from 'styled-components'

type ImageCarouselProps = ComponentPropsWithoutRef<'div'> & {
  imageIndex: number
  href?: string
  images: {
    src: string
    alt?: string
  }[]
}

const DOT_WIDTH = 8 // in px
const DOT_SPACING = 8 // in px
const DOTS_AMOUNT = 5
const DOTS_CENTER = Math.floor(DOTS_AMOUNT / 2)

const StyledImageCarousel = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  button {
    display: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  @media (hover: hover) {
    &:hover {
      button {
        display: inline-flex;
      }
    }
  }
`

const StyledSlidesWrapper = styled.div`
 //TODO: Check with Bart the background color when no image is loaded
  background: ${tok('semantics_views_tinted_background_color')};
  display: flex;
  height: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  scroll-snap-type: inline mandatory;
  overscroll-behavior-inline: contain;
  touch-action: manipulation;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledLeftButton = styled(IconButton)`
  left: ${space('primitives_space_12')};
`

const StyledRightButton = styled(IconButton)`
  right: ${space('primitives_space_12')};
`

const ImageCarouselSlide = styled.a`
  flex-basis: 100%;
  flex-shrink: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  text-decoration: none;
`

const StyledDotsWrapper = styled.div`
  position: absolute;
  bottom: ${space('primitives_space_16')};
  left: 50%;
  transform: translateX(-50%);
  width: ${DOT_WIDTH * DOTS_AMOUNT + DOT_SPACING * (DOTS_AMOUNT - 1)}px;
  overflow: hidden;
  display: flex;
`

const StyledDots = styled(SpaceBetween)`
  margin: auto;
  transition: transform 0.3s ease-in-out;
`

const StyledDot = styled.div`
  width: ${DOT_WIDTH}px;
  height: ${DOT_WIDTH}px; // circle
  border-radius: 100%;
  background: ${tok('primitives_color_neutral_0')};
  flex-shrink: 0;
  transition: opacity 0.2s;
`

export function ImageCarousel({ imageIndex, href, images, ...attributes }: ImageCarouselProps) {
  const slidesWrapperEl = useRef<HTMLDivElement>(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const [dotsWrapperStyle, setDotsWrapperStyle] = useState(0)

  useEffect(() => {
    const wrapper = slidesWrapperEl.current

    if (!wrapper)
      return

    const handleScroll = debounce(() => {
      const scrollPosition = wrapper.scrollLeft
      const wrapperWidth = wrapper.clientWidth
      const newActiveSlide = Math.round(scrollPosition / wrapperWidth)
      setActiveSlide(newActiveSlide)
    }, 10)

    wrapper.addEventListener('scroll', handleScroll)

    return () => {
      wrapper.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const scrollToSlide = (newActiveItem: number) => {
    if (!slidesWrapperEl.current)
      return

    const wrapperWidth = slidesWrapperEl.current.clientWidth
    slidesWrapperEl.current.scrollTo({
      behavior: 'smooth',
      left: wrapperWidth * newActiveItem,
    })
  }

  const onLeft = () => {
    const newActiveSlide = activeSlide === 0 ? images.length - 1 : activeSlide - 1
    scrollToSlide(newActiveSlide)
  }

  const onRight = () => {
    const newActiveSlide = activeSlide === images.length - 1 ? 0 : activeSlide + 1
    scrollToSlide(newActiveSlide)
  }

  useEffect(() => {
    if (activeSlide > DOTS_CENTER && activeSlide < images.length - DOTS_CENTER) {
      setDotsWrapperStyle((activeSlide - DOTS_CENTER) * (DOT_WIDTH + DOT_SPACING))
    }
    else if (activeSlide >= images.length - DOTS_CENTER) {
      setDotsWrapperStyle((images.length - DOTS_AMOUNT) * (DOT_WIDTH + DOT_SPACING))
    }
    else {
      setDotsWrapperStyle(0)
    }
  }, [activeSlide, images.length])

  return (
    <StyledImageCarousel {...attributes}>
      {images.length > 1 && (
        // TODO Review with Bart, we don't have design of the carrousel buttons
        <StyledLeftButton type="overlay-neutral" size="S" glyph="ChevronLeft" onClick={onLeft} />
      )}

      <StyledSlidesWrapper ref={slidesWrapperEl}>
        {/* Hack for no images show image without src */}
        {images.length < 1 && (
          <LazyImage src="" />
        )}
        {images.map((imageAttributes, index) => (
          <ImageCarouselSlide href={href} key={index} tabIndex={-1} aria-hidden target="_blank">
            {' '}
            {/* tabIndex: -1 to prevent unwanted keyboard navigation to the image */}
            <LazyImage
              {...imageAttributes}
              src={imageAttributes.src}
              srcSet={[520, 600, 750, 960]} // srcset values are a bit larger compared to the sizes, because most images are served in 3/2 format, but shown in something like 4/3 format (depending on height of information on ProductCard)
              sizes="(min-width: 600px) 255px, (min-width: 840px) 375px, 100vw"
              loading={index < 2 || activeSlide + 1 === index ? 'eager' : 'lazy'}
            />
            {' '}
          </ImageCarouselSlide>
        ))}
      </StyledSlidesWrapper>

      {images.length > 1 && (
        <StyledRightButton type="overlay-neutral" size="S" glyph="ChevronRight" onClick={onRight} />
      )}

      <StyledDotsWrapper className="c-image-carroussel-dots">
        <StyledDots
          direction="x"
          spacing={space('primitives_space_8')}
          style={{ transform: `translateX(-${dotsWrapperStyle}px)` }}
        >
          {images.map((_, index) => (
            <StyledDot key={index} style={{ opacity: index === activeSlide ? 1 : 0.56 }} />
          ))}
        </StyledDots>
      </StyledDotsWrapper>
    </StyledImageCarousel>
  )
}

ImageCarousel.displayName = 'ImageCarousel'
